/**
 * Manifest Co-Pilot mocks.
 *
 * The right-rail agent has:
 *   - SUGGESTED_PROMPTS: per-order context-appropriate prompts
 *   - INITIAL_THREADS: opening message from the agent ("I see…")
 *   - canned response lookup keyed by intent (matched against user input)
 *   - ACTION_HANDLERS: when the agent's response includes an action chip,
 *     that maps to an in-app effect (mark step done, post message, etc.)
 *
 * The CoPilot mocks intentionally lean into the workflow context — so
 * when the stakeholder types "what's blocking this" the response cites
 * the specific factory / step / day-of-N from the seeded order.
 */

export const SUGGESTED_PROMPTS = {
  'demo-radios': [
    "What's blocking this contract?",
    'Draft a counter-offer to Topsky at $130/unit',
    'Compare the 4 quotes side by side',
    'Generate a chase message to Yaesu Musen',
    'What certifications are mandatory here?',
  ],
  'demo-furniture': [
    'Production update — anything to flag?',
    'When will goods arrive in Ottawa?',
    'Generate the customs paperwork',
    'Draft delivery confirmation to DND',
    'Show me the contract docs status',
  ],
  'demo-streetlights': [
    'Generate the government invoice',
    'When should we expect payment?',
    'Has CBSA released the shipment?',
    'Summarize the contract financials',
    'Close out this contract',
  ],
};

export const INITIAL_THREADS = {
  'demo-radios': [
    {
      id: 'init1', sender: 'agent', ts: new Date().toISOString(),
      body: "Hi — I'm tracking the **RCMP tactical radios** contract.\n\nWe're on **Day 7 of 45**, currently in **Phase 2 (Quote & Negotiate)**. 4 of 5 quotes are in. The Hytera quote is **disqualified** (no MIL-STD-810). Topsky is the leader at $138 → I drafted a $130 counter and you sent it 30 minutes ago.\n\nAsk me anything, or pick a suggested prompt below.",
    },
  ],
  'demo-furniture': [
    {
      id: 'init1', sender: 'agent', ts: new Date().toISOString(),
      body: "Tracking the **DND ergonomic workstations** contract — Day 22 of 45, in **Phase 4 (Production)**.\n\nErgoMax is on schedule. I've reviewed the 3 production photo updates — no defects flagged. Pickup expected Day 32; delivery Day 45.\n\nWhat would you like to look at?",
    },
  ],
  'demo-streetlights': [
    {
      id: 'init1', sender: 'agent', ts: new Date().toISOString(),
      body: "**Mississauga LED streetlights** — Day 48 of 45 (over by 3 days but goods delivered).\n\nDelivery confirmed yesterday. POD attached. I've **already drafted the government invoice** (INV-2026-0142, $1.245M) and pre-filled the PSPC submission form. Just needs your signature.\n\nReady to close out?",
    },
  ],
};

// Intent matcher — naive keyword → response template lookup
export const RESPONSES = [
  {
    match: /block|stuck|delay|hold up/i,
    reply: (order) => {
      if (order.id === 'demo-radios') {
        return "Two things blocking right now:\n\n1. **Yaesu Musen** hasn't responded — last contact 5 days ago. I can send a chase via WhatsApp.\n2. **Topsky's counter response** is pending — you sent the $130 ask 30 min ago. Typical response time is 4–8h.\n\nNeither is urgent. Counter-offer reply is the gating event.";
      }
      if (order.id === 'demo-furniture') {
        return "Nothing blocking. Production is on track per ErgoMax's last 3 photo updates. The next dependency is the Day-30 QC inspection — I'll auto-schedule SGS once they confirm packing date.";
      }
      return "Only outstanding item is **government payment receipt**. Net-30 from invoice submission, so expect funds Day 75–78. I'll watch your bank feed and notify on receipt.";
    },
    actions: [],
  },
  {
    match: /counter|negotiat|\$130/i,
    reply: () => "Counter-offer drafted (in WhatsApp): _\"Mei — based on landed cost from competing quotes, can you do $130/unit at 350 qty?\"_\n\nI also pulled the comparison: at $130 vs $138, you save **$2,800 USD** = **~$3,850 CAD** landed. Worth the ask.",
    actions: [
      { id: 'send_counter', label: 'Send counter to Topsky' },
      { id: 'compare_quotes', label: 'Open quote comparison' },
    ],
  },
  {
    match: /compare.*quote|quote.*compare/i,
    reply: () => "Pulling up the side-by-side. Three real candidates after disqualifying Hytera:\n\n• **Topsky** — $138 USD, 28d, all certs. Best balance.\n• **Maxon** — $152 USD, 30d, all certs + CIF (insurance included).\n• **Vertex** — $198 USD, 21d, RCMP-grade. Premium option.\n\nTopsky is the recommended pick. Opening the Quotes tab.",
    actions: [{ id: 'open_quotes', label: 'Go to Quotes tab' }],
  },
  {
    match: /chase|nudge|follow.?up|yaesu/i,
    reply: () => "Drafted a chase message:\n\n> _\"Tanaka-san — following up on our radio RFQ from 5 days ago. We're closing the quote window at end of day. Are you able to participate?\"_\n\nReady to send via WhatsApp + email.",
    actions: [{ id: 'send_chase', label: 'Send chase to Yaesu' }],
  },
  {
    match: /cert|iso|compliance|mil.?std/i,
    reply: () => "RCMP requires three certifications for handheld radios:\n\n• **ISED RSS-119** (Canadian radio licensing — mandatory)\n• **IP67** (dust/water ingress — mandatory)\n• **MIL-STD-810H** drop/shock (RCMP-specific — mandatory)\n\nTopsky and Vertex have all three. **Hytera is DQ'd** — they don't carry MIL-STD-810. I've already filtered them from the active list.",
    actions: [],
  },
  {
    match: /production|update|photo/i,
    reply: () => "Three production photo updates received in the last 22 days:\n\n• **Day 7** — Frame welding line. No defects.\n• **Day 14** — Worksurface QC. Edge-banding consistent.\n• **Day 20** — Chair assembly. One armrest at slight angle, flagged for QC at Day 30.\n\nOverall confidence: high. ErgoMax is tracking on schedule.",
    actions: [{ id: 'open_production', label: 'Open Production tab' }],
  },
  {
    match: /arriv|eta|when.*ottawa|deliver/i,
    reply: () => "Based on ErgoMax's last update + ocean freight pattern:\n\n• Pickup: **Day 32** (~10 days)\n• Ningbo → Vancouver: **18 days** (LCL)\n• Vancouver → Ottawa rail: **5 days**\n• Customs + domestic handling: **2 days**\n\nETA Ottawa: **Day 57**. That's 12 days past the target deadline. I recommend asking ErgoMax about air-freighting the chair shipment to compress the timeline.",
    actions: [{ id: 'request_air_freight', label: 'Ask freighter for air option' }],
  },
  {
    match: /customs|cbsa|broker|import/i,
    reply: () => "Customs paperwork status for Mississauga LED:\n\n• **Commercial invoice** — Generated, signed by you ✓\n• **Certificate of origin** — Brightway provided ✓\n• **Packing list** — Auto-generated from Brightway's load plan ✓\n• **CBSA release order** — Issued by Cole International (broker) ✓\n\nGoods cleared customs on Day 44. Already attached to the Logistics tab.",
    actions: [{ id: 'open_logistics', label: 'Open Logistics tab' }],
  },
  {
    match: /invoice|generate.*invoice|government.*invoice/i,
    reply: () => "Government invoice **INV-2026-0142** generated:\n\n• Bill-to: PSPC (Marie Beaulieu)\n• Contract: EN578-23-0227\n• Amount: **$1,245,200 CAD** (GST/HST waived per ITB exemption)\n• Net-30 terms\n\nReady for e-signature + submission. The PSPC portal accepts electronic invoices via their iSupplier system — I can submit on your behalf once you sign.",
    actions: [
      { id: 'sign_invoice',   label: 'Sign invoice'  },
      { id: 'submit_invoice', label: 'Submit to PSPC' },
    ],
  },
  {
    match: /payment|paid|expect/i,
    reply: () => "Payment status: **Pending**.\n\nInvoice submitted today. PSPC standard terms are Net-30, which puts payment expected on **Day 75–78** (~4 weeks).\n\nI'll watch your TD Canada Trust feed and alert you the moment funds clear. Want me to also queue a status-check WhatsApp to Marie at Day 25 if no payment yet?",
    actions: [{ id: 'queue_followup', label: 'Queue Day-25 follow-up' }],
  },
  {
    match: /summar|overview|status/i,
    reply: (order) => `Here's where we are on **${order.contractTitle}**:\n\n• Day **${Math.floor((Date.now() - new Date(order.awardDate)) / 86400000)}** of 45\n• Contract value: **$${order.contractValueCad.toLocaleString()} CAD**\n• Quantity: ${order.quantity?.toLocaleString()}\n• Buyer: ${order.buyer}\n\nAsk me anything specific.`,
    actions: [],
  },
  {
    match: /close|wrap up|done|finished/i,
    reply: () => "Close-out checklist for this contract:\n\n• Invoice submitted ✓\n• POD on file ✓\n• Signature envelope sent ✓\n• Payment-watch armed ✓\n• Performance metrics calculated (45→48 days actual, +3 days)\n\nOnce payment lands, I'll auto-archive the contract to the closed pipeline. Anything else before we wrap?",
    actions: [{ id: 'archive_contract', label: 'Archive contract' }],
  },
];

export const FALLBACK_REPLY = (order) =>
  `I can help with anything on **${order.contractTitle}** — sourcing, negotiation, contract docs, freight, customs, invoicing, or status. What would you like to dig into?`;

// Action effects — keys match the `actions[].id` above. Each handler
// returns a string (toast message) and may dispatch context updates.
export const ACTION_EFFECTS = {
  send_counter:        { toast: 'Counter-offer sent to Topsky via WhatsApp.' },
  compare_quotes:      { toast: 'Switching to Quotes tab…', goTab: 'quotes' },
  open_quotes:         { toast: 'Switching to Quotes tab…', goTab: 'quotes' },
  send_chase:          { toast: 'Chase message sent to Yaesu Musen.' },
  open_production:     { toast: 'Switching to Production tab…', goTab: 'production' },
  request_air_freight: { toast: 'Air-freight request emailed to freighter.' },
  open_logistics:      { toast: 'Switching to Logistics tab…', goTab: 'logistics' },
  sign_invoice:        { toast: 'Signature pad opened — please sign.', openSig: true },
  submit_invoice:      { toast: 'Invoice submitted to PSPC iSupplier.' },
  queue_followup:      { toast: 'Day-25 payment follow-up queued.' },
  archive_contract:    { toast: 'Contract archived.' },
};
